import { useCallback, useEffect, useRef, useState } from 'react';
import { ResponseError } from 'api-client';

export async function profileFailure(failure: unknown, fallback: string): Promise<string> {
  if (failure instanceof ResponseError) {
    const body: unknown = await failure.response.json().catch((): null => null);
    if (body && typeof body === 'object') {
      const detail: unknown = Reflect.get(body, 'detail');
      if (typeof detail === 'string') return detail;
      const errors: unknown = Reflect.get(body, 'errors');
      if (Array.isArray(errors) && errors.every((error) => typeof error === 'string')) return errors.join(' ');
    }
  }
  return fallback;
}

// A retry never turns a failed read into an empty account. Ignore responses after unmount/client changes.
export function useProfileResource<T>(load: () => Promise<T>, failureMessage: string) {
  const generation = useRef<number>(0);
  const [attempt, setAttempt] = useState<number>(0);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    setData(null);
    void load()
      .then((value) => {
        if (current === generation.current) setData(value);
      })
      .catch(async (failure: unknown) => {
        const message = await profileFailure(failure, failureMessage);
        if (current === generation.current) setError(message);
      })
      .finally(() => {
        if (current === generation.current) setLoading(false);
      });
    return () => {
      generation.current += 1;
    };
  }, [load, attempt, failureMessage]);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  return { data, setData, loading, error, retry, generation };
}
